import { useState } from "react";
import {
  Phone, Mail, MapPin, Clock, Send, Loader2, Share2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import api from "@/api";

const SHARE_URL = typeof window !== "undefined" ? window.location.origin : "";

const Contact = () => {
  const [form, setForm] = useState({ nom: "", email: "", sujet: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setSent(true);
      setForm({ nom: "", email: "", sujet: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Vérifiez les champs du formulaire puis réessayez. Vous pouvez aussi nous appeler au 09 54 24 34 46."
      );
    } finally {
      setLoading(false);
    }
  };

  const share = async () => {
    const data = {
      title: "Aide au Monde et Santé — Maladie de Waldmann",
      text: "Soutenez les personnes atteintes de la maladie de Waldmann.",
      url: SHARE_URL,
    };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch {
        /* partage annulé */
      }
    } else {
      await navigator.clipboard.writeText(SHARE_URL);
      toast.success("Lien copié ! Partagez-le autour de vous.");
    }
  };

  const inputCls =
    "w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3.5 text-base font-medium text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#0284C7]";

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Parlons-nous, tout simplement"
        description="Une question, un besoin d'écoute, l'envie d'aider ? Écrivez-nous ou appelez-nous : nous répondons à tout le monde."
        testid="contact-title"
      />

      <section className="py-16 md:py-24">
        <div className="container-site grid items-start gap-12 lg:grid-cols-[1fr_0.9fr]">
          {/* Coordonnées */}
          <Reveal>
            <div className="space-y-5">
              <a
                href="tel:+33954243446"
                data-testid="contact-call-button"
                className="card-site group flex items-center gap-5 !p-6"
              >
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#10B981] transition-colors group-hover:bg-[#10B981] group-hover:text-white">
                  <Phone size={24} />
                </span>
                <div>
                  <p className="font-heading text-base font-bold text-[#0F2C59]">Appeler</p>
                  <p className="mt-0.5 font-heading text-xl font-extrabold text-[#0284C7]">09 54 24 34 46</p>
                  <p className="text-xs font-semibold text-slate-400">Touchez pour appeler depuis votre mobile</p>
                </div>
              </a>

              <a
                href="mailto:aide.monde.et.sante@gmail.com"
                data-testid="contact-email-link"
                className="card-site group flex items-center gap-5 !p-6"
              >
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#EFF6FF] text-[#0284C7] transition-colors group-hover:bg-[#0284C7] group-hover:text-white">
                  <Mail size={24} />
                </span>
                <div>
                  <p className="font-heading text-base font-bold text-[#0F2C59]">Écrire</p>
                  <p className="mt-0.5 text-base font-bold text-[#0284C7]">aide.monde.et.sante@gmail.com</p>
                </div>
              </a>

              <div className="card-site flex items-start gap-5 !p-6" data-testid="contact-address-card">
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#FFF1F1] text-[#FF6B6B]">
                  <MapPin size={24} />
                </span>
                <div>
                  <p className="font-heading text-base font-bold text-[#0F2C59]">Nous rencontrer</p>
                  <p className="mt-0.5 text-base leading-relaxed text-slate-600">
                    Aide au Monde et Santé
                    <br />
                    2 rue des Amandiers
                    <br />
                    95200 Sarcelles
                  </p>
                </div>
              </div>

              <div className="card-site flex items-center gap-5 !p-6" data-testid="contact-hours-card">
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-[#0F2C59]">
                  <Clock size={24} />
                </span>
                <div>
                  <p className="font-heading text-base font-bold text-[#0F2C59]">Horaires</p>
                  <p className="mt-0.5 text-base text-slate-600">Du lundi au vendredi, 9h00 – 18h00</p>
                </div>
              </div>

              <button
                type="button"
                onClick={share}
                data-testid="contact-share-button"
                className="btn-outline-navy w-full"
              >
                <Share2 size={17} /> Partager cette page autour de vous
              </button>
            </div>
          </Reveal>

          {/* Formulaire */}
          <Reveal delay={0.12}>
            {sent ? (
              <div className="card-site p-8 sm:p-12 text-center" data-testid="contact-success">
                <CheckCircle2 size={52} className="mx-auto text-[#10B981]" />
                <h2 className="mt-5 font-heading text-2xl font-extrabold text-[#0F2C59]">
                  Message bien reçu !
                </h2>
                <p className="mt-3 text-base leading-relaxed text-slate-600">
                  Merci pour votre message. Nous vous répondrons dans les meilleurs délais.
                  Pour une demande urgente, appelez le 09 54 24 34 46.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  data-testid="contact-new-message-button"
                  className="btn-outline-navy mt-7"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="card-site !p-7 sm:!p-9">
                <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2C59]">
                  Formulaire de contact
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Vos données ne servent qu'à vous répondre (voir notre politique de confidentialité).
                </p>
                <div className="mt-7 space-y-5">
                  <div>
                    <label htmlFor="contact-nom" className="mb-1.5 block text-sm font-bold text-slate-700">
                      Votre nom *
                    </label>
                    <input id="contact-nom" required minLength={2} value={form.nom} onChange={update("nom")} data-testid="contact-name-input" className={inputCls} placeholder="Prénom et nom" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-sm font-bold text-slate-700">
                      Votre e-mail *
                    </label>
                    <input id="contact-email" type="email" required value={form.email} onChange={update("email")} data-testid="contact-email-input" className={inputCls} placeholder="vous@exemple.fr" />
                  </div>
                  <div>
                    <label htmlFor="contact-sujet" className="mb-1.5 block text-sm font-bold text-slate-700">
                      Sujet *
                    </label>
                    <input id="contact-sujet" required minLength={2} value={form.sujet} onChange={update("sujet")} data-testid="contact-subject-input" className={inputCls} placeholder="Ex. : accompagnement, don, bénévolat…" />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="mb-1.5 block text-sm font-bold text-slate-700">
                      Votre message *
                    </label>
                    <textarea id="contact-message" required minLength={10} rows={5} value={form.message} onChange={update("message")} data-testid="contact-message-input" className={`${inputCls} resize-y`} placeholder="Écrivez votre message ici…" />
                  </div>
                  <button type="submit" disabled={loading} data-testid="contact-form-submit-button" className="btn-don w-full !py-4">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    Envoyer le message
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>

        {/* Carte */}
        <Reveal className="mt-14">
          <div className="overflow-hidden rounded-[2rem] border border-slate-200 shadow-sm" data-testid="contact-map">
            <iframe
              title="Carte — Aide au Monde et Santé, 2 rue des Amandiers, 95200 Sarcelles"
              src="https://maps.google.com/maps?q=2%20rue%20des%20Amandiers%2095200%20Sarcelles&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="h-[380px] w-full sm:h-[440px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
